import { useTranslation } from 'react-i18next';

/**
 * The partner-account endpoints return `Country` as a raw English name, and that same string is what
 * the account/sub-account lists filter and sort on and what the country filter submits back as its
 * option value. Per the i18n-conventions "config-/API-derived labels that double as logic keys" rule
 * the raw value is kept verbatim everywhere it's matched on, and only the *rendered* string is
 * localized — here, through the shared `Global.Countries` keys the account search form already uses.
 *
 * Only Canada and the United States occur on partner accounts, so those are the only two mapped; the
 * resolvers fall back to the raw value so any other country the API starts returning still renders.
 * The portal account list (`/portal/partner/accounts`) returns the alpha-2 abbreviation instead of
 * the name; `resolveCountryCodeLabel` covers that shape.
 */
export default function useCountryLabels() {
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const canada = tGlobal('Countries.CA');
  const unitedStates = tGlobal('Countries.USLong');

  const countries: Record<string, string> = {
    Canada: canada,
    'United States': unitedStates,
  };

  const countryCodes: Record<string, string> = {
    CA: canada,
    US: unitedStates,
  };

  return {
    resolveCountryLabel: (rawCountry: string): string => countries[rawCountry] ?? rawCountry,
    resolveCountryCodeLabel: (alpha2: string): string => countryCodes[alpha2] ?? alpha2,
  };
}
